import type { BackgroundDto, MediaDto } from '@studio/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Image as ImageIcon, Lock, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ImageSlots } from '@/components/ImageSlots';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/controls';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/form';
import { EmptyState, ErrorAlert, PageHeader, Skeleton } from '@/components/ui/misc';
import { api, errorMessage } from '@/lib/api';
import { useBackgrounds } from '@/lib/queries';

interface Draft {
  name: string;
  description: string;
  images: MediaDto[];
  notes: string;
}
const empty: Draft = { name: '', description: '', images: [], notes: '' };

function BackgroundDialog({
  background,
  open,
  onOpenChange,
}: {
  background: BackgroundDto | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [d, setD] = useState<Draft>(empty);
  useEffect(() => {
    if (open) setD(background ? { ...background } : empty);
  }, [open, background]);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));
  const save = useMutation({
    mutationFn: () => {
      const body = { name: d.name, description: d.description, notes: d.notes, imageIds: d.images.map((i) => i.id) };
      return background
        ? api(`/api/backgrounds/${background.id}`, { method: 'PATCH', body })
        : api('/api/backgrounds', { method: 'POST', body });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['backgrounds'] });
      void qc.invalidateQueries({ queryKey: ['characters'] });
      void qc.invalidateQueries({ queryKey: ['project'] });
      toast.success('Background saved');
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{background ? `Edit ${background.name}` : 'New background'}</DialogTitle>
          <DialogDescription>
            A place, shown by reference pictures. Lock it to characters on the Characters page: every still and shot
            with them is then set exactly here.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <Field label="Name">
            {(p) => <Input {...p} value={d.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Highway dhaba at night" />}
          </Field>
          <Field
            label="What it looks like (English)"
            hint="Goes into the prompts, e.g. a busy highway dhaba at night with string lights, plastic tables and a tandoor"
          >
            {(p) => <Textarea {...p} rows={3} value={d.description} onChange={(e) => set('description', e.target.value)} />}
          </Field>
          <div>
            <div className="mb-1.5 text-sm font-medium">Reference pictures (1–3)</div>
            <ImageSlots images={d.images} max={3} onChange={(images) => set('images', images)} />
            <p className="mt-1 text-xs text-muted-foreground">The first picture is the one the stills and shots copy the place from.</p>
          </div>
          <Field label="Notes">
            {(p) => <Textarea {...p} rows={2} value={d.notes} onChange={(e) => set('notes', e.target.value)} />}
          </Field>
        </div>
        <DialogFooter>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!d.name.trim()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Backgrounds: places with reference pictures, lockable to characters. */
export function BackgroundsPage() {
  const qc = useQueryClient();
  const q = useBackgrounds();
  const [editing, setEditing] = useState<BackgroundDto | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<BackgroundDto | null>(null);
  const del = useMutation({
    mutationFn: (b: BackgroundDto) => api(`/api/backgrounds/${b.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleting(null);
      void qc.invalidateQueries({ queryKey: ['backgrounds'] });
      void qc.invalidateQueries({ queryKey: ['characters'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Backgrounds"
        description="Places with reference pictures. Lock one to a character and every still and shot with them is set there."
        actions={
          <Button
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            <Plus /> New background
          </Button>
        }
      />
      {q.error && <ErrorAlert message={errorMessage(q.error)} />}
      {q.isLoading && <Skeleton className="h-48" />}
      {q.data && !q.data.length && <EmptyState icon={<ImageIcon />} title="No backgrounds yet" />}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {q.data?.map((b) => (
          <Card key={b.id} className="overflow-hidden">
            <button
              type="button"
              className="block w-full text-left"
              onClick={() => {
                setEditing(b);
                setOpen(true);
              }}
            >
              <div className="grid aspect-video place-items-center bg-muted">
                {b.images[0] ? (
                  <img src={b.images[0].url} alt="" className="size-full object-cover" />
                ) : (
                  <ImageIcon className="size-6 text-muted-foreground" />
                )}
              </div>
              <div className="space-y-1 p-3">
                <div className="font-medium">{b.name}</div>
                <p className="line-clamp-2 text-xs text-muted-foreground">{b.description || 'No description yet'}</p>
                <div className="flex flex-wrap gap-1">
                  <Badge variant="outline">
                    {b.images.length} picture{b.images.length === 1 ? '' : 's'}
                  </Badge>
                  {b.characters.map((c) => (
                    <Badge key={c.id} variant="secondary">
                      <Lock className="size-3" /> {c.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </button>
            <div className="flex justify-end border-t px-2 py-1">
              <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setDeleting(b)}>
                <Trash2 /> Delete
              </Button>
            </div>
          </Card>
        ))}
      </div>
      <BackgroundDialog background={editing} open={open} onOpenChange={setOpen} />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        title={`Delete ${deleting?.name}?`}
        description={
          deleting?.characters.length
            ? `It is locked to ${deleting.characters.map((c) => c.name).join(', ')}; they will have no background.`
            : 'It is not locked to any character.'
        }
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() => deleting && del.mutate(deleting)}
      />
    </div>
  );
}
